/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import React from 'react';
import { Select } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import zhCN from 'antd/lib/locale/zh_CN';
import zhTW from 'antd/lib/locale/zh_TW';
import { useStore } from '../stores/common';
import * as Selector from '../stores/selector';
import { Language } from '../constants';
import { ensureLocale } from '../i18n/i18n';

// Compact language picker for the standalone pages (Commons, Tutorial).
// Writes through to the same store the main app uses so the choice is
// remembered when the user returns to the modeling workspace. Kept here
// rather than inlined so both banners stay terse.
const StandaloneLanguageSelect = React.memo(({ size = 'middle' }: { size?: 'small' | 'middle' | 'large' }) => {
  const language = useStore(Selector.language);
  const setCommonStore = useStore(Selector.set);

  return (
    <Select
      size={size}
      variant="borderless"
      value={language}
      onChange={(value) => {
        // Non-English translation tables are code-split; kick off the fetch
        // before the store flips so the strings are usually there by the time
        // the re-render lands. ensureLocale re-emits languageChanged when the
        // bundle arrives, so a slow fetch just means a brief English frame.
        ensureLocale(value);
        setCommonStore((state) => {
          state.language = value;
          switch (value) {
            case 'zh_cn':
              state.locale = zhCN;
              break;
            case 'zh_tw':
              state.locale = zhTW;
              break;
            default:
              state.locale = enUS;
          }
        });
      }}
      style={{ minWidth: 96 }}
      options={[
        { value: 'en', label: Language.English },
        { value: 'zh_cn', label: Language.ChineseSimplified },
        { value: 'zh_tw', label: Language.ChineseTraditional },
      ]}
    />
  );
});

export default StandaloneLanguageSelect;
